import { useState, useEffect, useCallback } from 'react';
import './Team.css';
import EmployeeService from '../AppSettings/services/EmployeeService';
import RateService from '../AppSettings/services/RateService';
import MicrosoftHttpService from '../AppSettings/services/MicrosoftHttpService';
import { i18nService } from '../AppSettings/services/i18nService';
import { useError } from '../Context/ErrorContext';
import useActiveEmployeeId from '../AppSettings/hooks/useActiveEmployeeId';
import { isFeatureEnabled } from '../AppSettings/environmentConfig';
import {
  TeamSearchBar,
  TeamEmployeesTab,
  TeamContactsTab,
  CompanyRatesModal,
  CreateContactModal,
  AddFieldMemberModal,
  EnableSigninModal,
  EmployeeRatesModal,
} from './Components';
import type {
  SearchType,
  ActiveTab,
  MicrosoftContact,
  Employees,
  Employee,
  Permission,
  Rate,
  ErrorContextValue,
} from '../types/team';

const ITEMS_PER_PAGE = 5;

const Team = (): JSX.Element => {
  /** Field members (Build M) are a pipeline feature — hide the flows on non-pipeline builds. */
  const fieldMembersEnabled = isFeatureEnabled('bids');
  const employeeId = useActiveEmployeeId();
  const [currentUser, setCurrentUser] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employees[]>([]);
  const [contacts, setContacts] = useState<MicrosoftContact[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [contactsPage, setContactsPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [contactsLoading, setContactsLoading] = useState<boolean>(true);
  const [globalSearchTerm, setGlobalSearchTerm] = useState<string>('');
  const [searchType, setSearchType] = useState<SearchType>('all');
  const [activeTab, setActiveTab] = useState<ActiveTab>('team');
  const [showNewContactForm, setShowNewContactForm] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showRatesModal, setShowRatesModal] = useState<boolean>(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState<boolean>(false);
  const [memberForSignin, setMemberForSignin] = useState<Employees | null>(null);
  const [showEmployeeRatesModal, setShowEmployeeRatesModal] = useState<boolean>(false);
  const [selectedEmployeeForRates, setSelectedEmployeeForRates] = useState<Employees | null>(null);
  const [rates, setRates] = useState<Rate[]>([]);

  const { setError } = useError() as unknown as ErrorContextValue;

  const fetchEmployees = useCallback(async (): Promise<void> => {
    try {
      const data = await EmployeeService.getEmployees();
      setEmployees(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setLoading(false);
      setError(error);
    }
  }, [setError]);

  const fetchContacts = useCallback(async (): Promise<void> => {
    try {
      const microsoftService = new MicrosoftHttpService([
        'https://graph.microsoft.com/Contacts.Read',
      ]);

      const contactsData = await microsoftService.getContacts();
      setContacts(contactsData.value || []);
      setContactsLoading(false);
    } catch (error) {
      console.error(i18nService.t('errors.services.contacts.fetching'), error);
      setContactsLoading(false);
    }
  }, []);

  const fetchPermissions = useCallback(async (): Promise<void> => {
    try {
      const data = await EmployeeService.getPermissions();
      setPermissions(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  }, []);

  const fetchRates = useCallback(async (): Promise<void> => {
    try {
      const data = await RateService.getRates();
      setRates(data);
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  }, []);

  const fetchCurrentUser = useCallback(async (): Promise<void> => {
    try {
      if (!employeeId) {
        setCurrentUser(null);
        return;
      }

      const userData = await EmployeeService.getEmployeeById(employeeId);
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchEmployees();
    fetchContacts();
    fetchPermissions();
    fetchRates();
  }, [fetchEmployees, fetchContacts, fetchPermissions, fetchRates]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const handlePermissionUpdate = async (
    empId: string,
    newPermishId: number
  ): Promise<void> => {
    try {
      await EmployeeService.updateEmployeePermish(empId, newPermishId);
      await fetchEmployees();
      setActiveDropdown(null);
    } catch (error) {
      console.error('Error updating permission:', error);
    }
  };

  const handleAssignRates = (employee: Employees): void => {
    setSelectedEmployeeForRates(employee);
    setShowEmployeeRatesModal(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement;
      if (activeDropdown && !target.closest('.team-permissions-dropdown')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  const filteredEmployees = employees.filter((employee) => {
    const searchStr = globalSearchTerm.toLowerCase();
    if (!searchStr) return true;

    switch (searchType) {
      case 'id':
        return employee.EmployeeID.toLowerCase().includes(searchStr);
      case 'name':
        return `${employee.FirstName} ${employee.LastName}`
          .toLowerCase()
          .includes(searchStr);
      case 'email':
        // Nullable since Build M: a field member may carry no email at all.
        return (employee.Email ?? '').toLowerCase().includes(searchStr);
      case 'phone':
        return (
          employee.Phone && employee.Phone.toLowerCase().includes(searchStr)
        );
      default:
        return (
          employee.EmployeeID.toLowerCase().includes(searchStr) ||
          `${employee.FirstName} ${employee.LastName}`
            .toLowerCase()
            .includes(searchStr) ||
          (employee.Email ?? '').toLowerCase().includes(searchStr) ||
          (employee.Phone && employee.Phone.toLowerCase().includes(searchStr))
        );
    }
  });

  const filteredContacts = contacts.filter((contact) => {
    const searchStr = globalSearchTerm.toLowerCase();
    if (!searchStr) return true;

    const fullName =
      contact.givenName && contact.surname
        ? `${contact.givenName} ${contact.surname}`
        : contact.displayName || '';
    const email =
      contact.emailAddresses && contact.emailAddresses[0]
        ? contact.emailAddresses[0].address
        : '';
    const phone =
      contact.mobilePhone ||
      (contact.businessPhones && contact.businessPhones[0]) ||
      (contact.homePhones && contact.homePhones[0]) ||
      '';

    switch (searchType) {
      case 'name':
        return fullName.toLowerCase().includes(searchStr);
      case 'email':
        return email.toLowerCase().includes(searchStr);
      case 'phone':
        return phone.toLowerCase().includes(searchStr);
      case 'company':
        return (contact.companyName || '').toLowerCase().includes(searchStr);
      default:
        return (
          fullName.toLowerCase().includes(searchStr) ||
          email.toLowerCase().includes(searchStr) ||
          phone.toLowerCase().includes(searchStr) ||
          (contact.companyName || '').toLowerCase().includes(searchStr) ||
          (contact.jobTitle || '').toLowerCase().includes(searchStr)
        );
    }
  });

  const indexOfLastEmployee = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstEmployee = indexOfLastEmployee - ITEMS_PER_PAGE;
  const currentEmployees = filteredEmployees.slice(
    indexOfFirstEmployee,
    indexOfLastEmployee
  );
  const totalEmployeePages = Math.ceil(filteredEmployees.length / ITEMS_PER_PAGE);

  const indexOfLastContact = contactsPage * ITEMS_PER_PAGE;
  const indexOfFirstContact = indexOfLastContact - ITEMS_PER_PAGE;
  const currentContacts = filteredContacts.slice(
    indexOfFirstContact,
    indexOfLastContact
  );
  const totalContactPages = Math.ceil(filteredContacts.length / ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
    setContactsPage(1);
  }, [globalSearchTerm, searchType]);

  return (
    <div className="team-wrapper">
      <TeamSearchBar
        globalSearchTerm={globalSearchTerm}
        onSearchTermChange={setGlobalSearchTerm}
        searchType={searchType}
        onSearchTypeChange={setSearchType}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        employeesCount={filteredEmployees.length}
        contactsCount={filteredContacts.length}
        onOpenRatesModal={() => setShowRatesModal(true)}
        showCompanyRatesButton={
          currentUser != null && Number(currentUser.permish_id) === 1
        }
      />

      <div className="team-section-wrapper">
        <div
          className={`team-tab-content ${activeTab === 'team' ? 'active' : ''}`}
        >
          <TeamEmployeesTab
            loading={loading}
            employees={currentEmployees}
            currentUser={currentUser}
            permissions={permissions}
            rates={rates}
            activeDropdown={activeDropdown}
            onDropdownToggle={setActiveDropdown}
            onPermissionUpdate={handlePermissionUpdate}
            onAssignRates={handleAssignRates}
            onAddMember={
              fieldMembersEnabled ? () => setShowAddMemberModal(true) : undefined
            }
            onEnableSignin={
              fieldMembersEnabled
                ? (employee) => setMemberForSignin(employee)
                : undefined
            }
            currentPage={currentPage}
            totalPages={totalEmployeePages}
            onPageChange={setCurrentPage}
          />
        </div>

        <div
          className={`team-tab-content ${activeTab === 'contacts' ? 'active' : ''}`}
        >
          <TeamContactsTab
            loading={contactsLoading}
            contacts={currentContacts}
            currentPage={contactsPage}
            totalPages={totalContactPages}
            onPageChange={setContactsPage}
            onAddContact={() => setShowNewContactForm(true)}
          />
        </div>
      </div>

      <CreateContactModal
        isOpen={showNewContactForm}
        onClose={() => setShowNewContactForm(false)}
        onContactCreated={fetchContacts}
      />

      {fieldMembersEnabled && (
        <>
          <AddFieldMemberModal
            isOpen={showAddMemberModal}
            onClose={() => setShowAddMemberModal(false)}
            onMemberCreated={fetchEmployees}
          />

          <EnableSigninModal
            isOpen={memberForSignin != null}
            member={memberForSignin}
            permissions={permissions}
            onClose={() => setMemberForSignin(null)}
            onEnabled={fetchEmployees}
          />
        </>
      )}

      <CompanyRatesModal
        isOpen={showRatesModal}
        onClose={() => setShowRatesModal(false)}
      />

      <EmployeeRatesModal
        isOpen={showEmployeeRatesModal}
        onClose={() => {
          setShowEmployeeRatesModal(false);
          setSelectedEmployeeForRates(null);
        }}
        employee={selectedEmployeeForRates}
        onRatesUpdated={fetchEmployees}
      />
    </div>
  );
};

export default Team;
